import React, { useMemo } from 'react';
import './FeaturedCrossingCardPreview.css';

const MODE_META = {
  passenger: {
    labelKey: 'vehicular_crossing',
    icon: '/redesign/icon-car.svg',
  },
  pedestrian: {
    labelKey: 'pedestrian_crossing_label',
    icon: '/redesign/icon-ped.svg',
  },
  commercial: {
    labelKey: 'commercial_crossing',
    icon: '/redesign/icon-truck.svg',
  },
};

const LANE_LABEL_KEYS = {
  standard: 'lane_standard',
  ready: 'lane_ready',
  sentri: 'lane_sentri',
  fast: 'lane_fast',
};

function formatWaitTime(minutes, labels) {
  if (minutes == null || Number.isNaN(Number(minutes))) return labels.not_available;
  const mins = Number(minutes);
  if (mins === 0) return labels.no_wait;
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (rem === 0) return `${hours} h`;
  return `${hours} h ${rem} min`;
}

function waitColor(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return '#9ca3af';
  const m = Number(minutes);
  if (m <= 30) return '#3db35a';
  if (m <= 60) return '#d97706';
  return '#dc2626';
}

function resolveDelay(lane) {
  const raw = lane?.delay_minutes_original ?? lane?.delay_minutes;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function hoursBadge(hours, labels) {
  if (!hours) return labels.not_available;
  if (hours === '24 hrs/day') return '24/7';
  return hours;
}

function trendLabel(status, labels) {
  if (status === 'rising') return labels.trend_rising;
  if (status === 'falling') return labels.trend_falling;
  return labels.trend_stable;
}

function trendIcon(status) {
  if (status === 'rising') return '/redesign/icon-trend-up.svg';
  if (status === 'falling') return '/redesign/icon-trend-down.svg';
  return '/redesign/icon-trend-stable.svg';
}

function trendIconClass(status) {
  if (status === 'falling') return 'fcc-trend-icon fcc-trend-icon--falling';
  return 'fcc-trend-icon';
}

function formatOpenLines(lanesOpen, labels) {
  if (lanesOpen == null || Number.isNaN(Number(lanesOpen))) return null;
  const n = Number(lanesOpen);
  if (n === 0) return labels.no_lines;
  if (n === 1) return `1 ${labels.line_label}`;
  return `${n} ${labels.lines_label}`;
}

function buildGroups(crossing) {
  const byMode = { passenger: [], pedestrian: [], commercial: [] };
  (crossing?.ports || []).forEach((port) => {
    (port?.wait_time?.lanes || []).forEach((laneGroup) => {
      const mode = laneGroup?.transport_mode;
      if (!byMode[mode]) return;
      const visible = port.visibility?.[mode] || [];
      if (!visible.length) return;
      (laneGroup.lane_types || []).forEach((lt) => {
        if (visible.includes(lt.type)) {
          byMode[mode].push({ ...lt, _port: port });
        }
      });
    });
  });
  return byMode;
}

export default function FeaturedCrossingCardPreview({
  crossing,
  cityLabel,
  departureCity,
  image,
  labels,
  href = '#',
}) {
  const status = crossing?.ports?.[0]?.wait_time?.port_status;
  const hours = crossing?.ports?.[0]?.wait_time?.hours;
  const isOpen = status === 'Open' || status == null;
  const groups = useMemo(() => buildGroups(crossing), [crossing]);
  const modes = ['passenger', 'pedestrian', 'commercial'].filter(
    (m) => (groups[m] || []).length > 0
  );

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="fcc-card">
      <div className="fcc-card__media">
        {image?.url ? (
          <img
            src={image.url}
            alt={image.alt || crossing?.name || ''}
            className="fcc-card__img"
          />
        ) : (
          <div className="fcc-card__img-placeholder">No image</div>
        )}
        <div className="fcc-card__badge">
          <span
            className={`fcc-card__badge-dot ${
              isOpen ? 'fcc-card__badge-dot--open' : 'fcc-card__badge-dot--closed'
            }`}
          />
          <span className="fcc-card__badge-text">
            {isOpen ? labels.open : labels.closed}
            {' • '}
            {hoursBadge(hours, labels)}
          </span>
        </div>
      </div>

      <div className="fcc-card__body">
        {departureCity ? (
          <p className="fcc-card__eyebrow">
            {labels.garita_prefix} {departureCity}
          </p>
        ) : null}
        <h3 className="fcc-card__title">{cityLabel || crossing?.name || '—'}</h3>
        {crossing?.description ? (
          <p className="fcc-card__desc">{crossing.description}</p>
        ) : null}

        {modes.length === 0 && (
          <div className="fcc-card__empty">
            No lane data for preview (save entry page ports / wait times).
          </div>
        )}

        {modes.map((mode) => (
          <div key={mode} className="fcc-mode">
            <div className="fcc-mode__head">
              <img src={MODE_META[mode].icon} alt="" width={16} height={16} aria-hidden />
              <span className="fcc-mode__label">{labels[MODE_META[mode].labelKey]}</span>
              <span className="fcc-mode__rule" />
            </div>
            {(groups[mode] || []).map((lane, idx) => {
              const delay = resolveDelay(lane);
              const trendStatus = lane?._port?.trend?.[lane?.type]?.status || 'stable';
              const lines = formatOpenLines(lane?.lanes_open, labels);
              return (
                <div key={`${mode}-${lane.type}-${idx}`} className="fcc-lane">
                  <div className="fcc-lane__left">
                    <div className="fcc-lane__name">
                      {labels[LANE_LABEL_KEYS[lane.type] || 'lane_standard']}
                    </div>
                    <div className="fcc-lane__meta">
                      {lines ? <span>{lines}</span> : null}
                      <span className={trendIconClass(trendStatus)}>
                        <img
                          src={trendIcon(trendStatus)}
                          alt=""
                          width={12}
                          height={12}
                          aria-hidden
                        />
                      </span>
                      <span>{trendLabel(trendStatus, labels)}</span>
                    </div>
                  </div>
                  <div
                    className="fcc-lane__wait"
                    style={{ color: waitColor(delay) }}
                  >
                    {formatWaitTime(delay, labels)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </a>
  );
}
